import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { EventBusService } from 'src/app/services/eventBus.service';
import { SharedModule } from 'src/app/shared/shared.module';
import { AuthStore } from 'src/app/store/auth.store';
import { BankAccountsStore } from 'src/app/store/bank-accounts.store';
import { BankBeneficiariesStore } from 'src/app/store/bank-beneficiaries.store';
import { BankExtractionRequestsStore } from 'src/app/store/bank-extraction-requests.store';
import { BeneficiariesStore } from 'src/app/store/beneficiaries.store';
import { ExtractionRequestsStore } from 'src/app/store/extraction-requests.store';
import { MovementsStore } from 'src/app/store/movements.store';
import { PaymentLinkStore } from 'src/app/store/payment-link.store';
import { SiteStore } from 'src/app/store/site.store';
import { environment } from 'src/environments/environment';
import { AccountSelectionDialogComponent } from '../account-selection-dialog/account-selection-dialog.component';
import { CaretDownIconComponent } from '../icons/caret-down-icon.component';
import { WalletIconComponent } from '../icons/wallet-icon.component';

@Component({
  selector: 'app-bank-account-button',
  standalone: true,
  imports: [SharedModule, WalletIconComponent, CaretDownIconComponent],
  templateUrl: './bank-account-button.component.html',
  styleUrl: './bank-account-button.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BankAccountButtonComponent {
  siteStore = inject(SiteStore);
  bankAccountsStore = inject(BankAccountsStore);
  private dialog = inject(MatDialog);
  private router = inject(Router);
  eventBus = inject(EventBusService);
  authStore = inject(AuthStore);
  balanceAnimationTrigger = signal<number | null>(null);
  private destroyRef = inject(DestroyRef);

  // Stores to clear on account change
  private beneficiariesStore = inject(BeneficiariesStore);
  private bankBeneficiariesStore = inject(BankBeneficiariesStore);
  private movementsStore = inject(MovementsStore);
  private extractionRequestsStore = inject(ExtractionRequestsStore);
  private bankExtractionRequestsStore = inject(BankExtractionRequestsStore);
  private paymentLinkStore = inject(PaymentLinkStore);

  showBalance = computed(
    () =>
      !this.authStore.user().metadata.applicationsMetadata?.[environment.appId]?.hiddeBalanceAlways
  );

  async ngOnInit() {
    this.eventBus.on('current_balance_updated')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((newBalance) => {
        const currentTrigger = this.balanceAnimationTrigger() || 0;
        this.balanceAnimationTrigger.set(currentTrigger + 1);
      });
  }

  formatedAccountNumber() {
    const accountNumber = this.bankAccountsStore.selectedBankAccount()?.accountNumber;
    // i want a format like 1234****5678
    return accountNumber.slice(0, 4) + '****' + accountNumber.slice(-4);
  }

  openBankAccountDialog() {
    const dialogRef = this.dialog.open(AccountSelectionDialogComponent, {
      width: '500px',
      maxWidth: '90vw',
      panelClass: 'app-sheet-container',
      data: {
        title: 'Seleccionar Cuenta',
        description: 'Elige la cuenta que deseas utilizar',
        accounts: this.bankAccountsStore.bankAccounts() || [],
        selectedAccountId: this.bankAccountsStore.selectedBankAccountId()
      }
    });

    dialogRef.afterClosed().subscribe(async (result) => {
      if (result) {
        // Limpiar stores antes de actualizar la cuenta seleccionada
        this.beneficiariesStore.clearStore();
        this.bankBeneficiariesStore.clearStore();
        this.movementsStore.clearStore();
        this.extractionRequestsStore.clearStore();
        this.bankExtractionRequestsStore.clearStore();
        this.paymentLinkStore.clearStore();

        // Actualizar la cuenta seleccionada en el store
        this.bankAccountsStore.setSelectedBankAccountNumber(result.accountNumber);

        // Refrescar stores según la ruta actual
        const currentUrl = this.router.url;

        // Siempre recargar beneficiarios, extraction requests y transacciones (movements)
        this.beneficiariesStore.clearFilters();
        this.extractionRequestsStore.clearFilters();
        this.movementsStore.clearFilters();

        await Promise.all([
          this.beneficiariesStore.reloadBeneficiaries(),
          this.extractionRequestsStore.reloadExtractionRequests(),
        ]);

        // Recargar solicitudes de pago bancario y beneficiarios bancarios si el usuario es de tipo business
        if (this.authStore.isBusiness()) {
          await Promise.all([
            this.bankExtractionRequestsStore.reloadBankExtractionRequests(),
            this.bankBeneficiariesStore.reloadBankBeneficiaries()
          ]);
        }

        // Solo recargar paylinks si estamos en la página de paylinks
        if (currentUrl.includes('/paylinks')) {
          await this.paymentLinkStore.loadPaymentLinks();
        }
      }
    });
  }
}
